import { useCallback, useMemo } from 'react'
import {
  Badge,
  Button,
  Copy,
  Icon,
  Table,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip,
  Typography
} from '@percent/lemonade'

import { dayJS } from '@percent/cause-dashboard/common/utility/date'
import { useTranslation } from 'react-i18next'

import styles from '../../PayoutsPage.module.scss'
import { DonationsIncludedListProps } from './DonationsIncludedList.types'
import { DonationWithFees } from '@percent/cause-dashboard/services/payouts/payoutsService.types'
import { currencyMapToFormat } from '@percent/cause-dashboard/common/utility/money/currencyMap'
import { useCSVDownloader } from '@percent/cause-dashboard/common/hooks/useCSVDownloader/useCSVDownloader'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useAuthState } from '@percent/cause-dashboard/common/hooks'

export function DonationsIncludedList({
  currencyMap,
  data,
  totalResults,
  nextPage,
  previousPage,
  payoutId,
  payoutDate
}: DonationsIncludedListProps) {
  const { t } = useTranslation()
  const {
    authState: { user }
  } = useAuthState()
  const {
    reportingService: { getPayoutDonations }
  } = useServices()

  const [{ loading: csvDownloaderLoading }, { fetchPayoutDonationsCSV }] = useCSVDownloader({
    service: getPayoutDonations(user?.organisationId as string, payoutId),
    fileName: () => `payout_report_${payoutDate}`,
    successMessage: t('toast.payoutDonationsListExportSuccess'),
    errorMessage: t('toast.payoutDonationsListExportError')
  })

  const handleExport = useCallback(() => {
    if (fetchPayoutDonationsCSV) {
      fetchPayoutDonationsCSV()
    }
  }, [fetchPayoutDonationsCSV])

  const columns = useMemo(
    () => [
      { id: t('typography.grossDonation'), isSortable: false, props: { width: '14%' } },
      {
        id: t('typography.fees'),
        isSortable: false,
        content: (
          <Tooltip content={t('typography.feesInfo')}>
            <div className={styles.feesTooltip}>
              <Typography variant="default-medium">{t('typography.fees')}</Typography>
              <Icon name="info" size={5} />
            </div>
          </Tooltip>
        ),
        props: { width: '10%' }
      },
      { id: t('typography.donationsTableDonorName'), isSortable: false, props: { width: '12%' } },
      { id: t('typography.donationsTableDonorEmail'), isSortable: false, props: { width: '12%' } },
      { id: t('typography.donationsTableGiftNote'), isSortable: false, props: { width: '23%' } },
      { id: t('typography.donationsTableMarketingConsent'), isSortable: false, props: { width: '14%' } },
      { id: t('typography.donationsTablePartner'), isSortable: false, props: { width: '14%' } },
      { id: t('typography.donationsTableDonationCreated'), isSortable: false, props: { width: '12%' } }
    ],
    [t]
  )

  const moneyFormat = currencyMapToFormat(currencyMap)

  return (
    <TableProvider
      isLoading={false}
      totalResults={totalResults}
      columns={columns}
      emptyTableTitle=""
      illustration="no-results"
    >
      <div className={styles.tableHeader}>
        <Typography variant="h3">{t('typography.donationsIncluded')}</Typography>
        <Button
          size="small"
          variant="secondary"
          data-testid="export-payout-donations-btn"
          onClick={handleExport}
          loading={csvDownloaderLoading}
          disabled={csvDownloaderLoading}
        >
          {t('button.exportCsv')}
        </Button>
      </div>
      <Table>
        {data?.map(
          ({
            id,
            partner,
            partnerId,
            fee,
            amount,
            settledAmount,
            firstName,
            lastName,
            email,
            consentedToBeContactedByOrg,
            createdAt,
            giftNote
          }: DonationWithFees) => {
            const currency = settledAmount?.currency ?? amount.currency
            const grossAmount = fee?.grossAmount ?? settledAmount ?? amount
            const feeAmount = fee?.feeAmount ?? { amount: 0, currency }

            return (
              <TableRow key={`donation-list-${id}`} className={styles.listItem}>
                <TableCell>{moneyFormat(grossAmount)}</TableCell>
                <TableCell>{moneyFormat(feeAmount)}</TableCell>
                <TableCell>
                  {firstName && lastName ? (
                    `${firstName} ${lastName}`
                  ) : (
                    <Badge variant="default" icon="view-off">
                      {t('typography.anonymous')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {email || (
                    <Badge variant="default" icon="view-off">
                      {t('typography.anonymous')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Typography className={styles.giftNote}>
                    {giftNote && (
                      <Tooltip content={giftNote} trigger="hover">
                        <div className={styles.tooltipContent}>
                          <Copy text={giftNote ?? ''} className={styles.smallCopy}>
                            {giftNote ?? ''}
                          </Copy>
                        </div>
                      </Tooltip>
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={consentedToBeContactedByOrg === 'yes' ? 'positive' : 'critical'}
                    icon={consentedToBeContactedByOrg === 'yes' ? 'approve' : 'reject'}
                  >
                    {t(`typography.${consentedToBeContactedByOrg === 'yes' ? 'yes' : 'no'}`)}
                  </Badge>
                </TableCell>
                <TableCell>{partner?.name || partnerId.substring(0, 12) + '...'}</TableCell>
                <TableCell>{dayJS(createdAt).format('DD MMM YYYY')}</TableCell>
              </TableRow>
            )
          }
        )}
      </Table>
      <TablePagination
        previousPage={previousPage}
        nextPage={nextPage}
        totalResultsText={t('typography.results', { count: totalResults })}
      />
    </TableProvider>
  )
}
